import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DecorativeCubes from '../components/DecorativeCubes';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExploreIcon from '@mui/icons-material/Explore';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PaletteIcon from '@mui/icons-material/Palette';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import GroupsIcon from '@mui/icons-material/Groups';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ImageIcon from '@mui/icons-material/Image';
import axios from '../api/axios';

// Полный URL для изображения
const getFullImageSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads')) return url;
  if (url.startsWith('uploads')) return '/' + url;
  if (url.startsWith('/')) return url;
  return url;
};

const CATEGORIES = [
  {
    number: '01',
    name: 'Нужна помощь специалиста',
    description: 'Диагностика, консультирование, методическая поддержка',
    icon: SupportAgentIcon,
    color: '#00ACC1',
  },
  {
    number: '02',
    name: 'Хочу лучше понимать ребёнка',
    description: 'Воспитание, эмоции, поведение, поддержка родителей',
    icon: FavoriteIcon,
    color: '#EC407A',
  },
  {
    number: '03',
    name: 'Мир творчества',
    description: 'Музыка, танцы, рисование, театр, декоративно-прикладное творчество',
    icon: PaletteIcon,
    color: '#FFA726',
  },
  {
    number: '04',
    name: 'Спорт и движение',
    description: 'Физическое развитие, спорт, активный образ жизни',
    icon: DirectionsRunIcon,
    color: '#66BB6A',
  },
  {
    number: '05',
    name: 'Общение и развитие',
    description: 'Раннее развитие, дополнительное образование, социально-гуманитарные программы',
    icon: GroupsIcon,
    color: '#7E57C2',
  },
  {
    number: '06',
    name: 'Особые образовательные потребности и поддержка семьи',
    description: 'Дети с ОВЗ, инклюзивное образование, права семьи, кризисные ситуации',
    icon: VolunteerActivismIcon,
    color: '#42A5F5',
  },
];

const UsefulResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const response = await axios.get('/useful-resources/public');
        setResources(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Ошибка загрузки ресурсов:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, []);

  const handleOpenCategory = (category) => setActiveCategory(category);
  const handleCloseCategory = () => setActiveCategory(null);

  const itemsForActiveCategory = activeCategory
    ? resources.filter((r) => r.category === activeCategory.name)
    : [];

  const otherResources = resources.filter((r) => r.category === 'Другое');

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <CircularProgress />
          </Box>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <DecorativeCubes minCubes={6} maxCubes={10} />
      <Header />

      <Box sx={{ position: 'relative', minHeight: '100vh', backgroundColor: '#f9f9f9', zIndex: 2 }}>
        {/* Простая шапка раздела */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #00ACC1 0%, #81C784 50%, #C5E1A5 100%)',
            py: { xs: 6, md: 8 },
            textAlign: 'center',
          }}
        >
          <Container maxWidth="md">
            <ExploreIcon sx={{ fontSize: 48, color: 'white', mb: 2, opacity: 0.9 }} />
            <Typography
              variant="h2"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 1.5,
                fontSize: { xs: '1.9rem', md: '2.5rem' },
              }}
            >
              Навигатор для родителей
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.95)',
                fontWeight: 400,
                fontSize: { xs: '0.95rem', md: '1.15rem' },
              }}
            >
              Выберите направление, которое ближе к вашему вопросу, и узнайте, куда можно обратиться
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 6, position: 'relative', zIndex: 2 }}>
          <Grid container spacing={3}>
            {CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <Grid item xs={12} sm={6} lg={4} key={category.number}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      transition: 'transform 0.25s, box-shadow 0.25s',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 10px 28px rgba(0,0,0,0.14)',
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography
                          sx={{
                            fontSize: '1.75rem',
                            fontWeight: 700,
                            color: category.color,
                            opacity: 0.5,
                          }}
                        >
                          {category.number}
                        </Typography>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            backgroundColor: `${category.color}1A`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Icon sx={{ color: category.color, fontSize: 26 }} />
                        </Box>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {category.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                        {category.description}
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => handleOpenCategory(category)}
                        sx={{
                          backgroundColor: category.color,
                          alignSelf: 'flex-start',
                          '&:hover': { backgroundColor: category.color, opacity: 0.9 },
                        }}
                      >
                        Подробнее
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {otherResources.length > 0 && (
            <Box sx={{ mt: 8 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, textAlign: 'center', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Другие полезные ресурсы
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                Дополнительные материалы и ссылки
              </Typography>
              <Grid container spacing={3}>
                {otherResources.map((resource) => (
                  <Grid item xs={12} md={6} lg={4} key={resource._id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 3,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      {resource.imageUrl ? (
                        <Box
                          component="img"
                          src={getFullImageSrc(resource.imageUrl)}
                          alt={resource.title}
                          onError={(e) => { e.target.style.display = 'none'; }}
                          sx={{
                            width: '100%',
                            height: 135,
                            objectFit: 'contain',
                            backgroundColor: '#f5f5f5',
                            p: 1,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: 135,
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 36, opacity: 0.3 }} />
                        </Box>
                      )}
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          {resource.title}
                        </Typography>
                        {resource.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                            {resource.description}
                          </Typography>
                        )}
                        {resource.url && (
                          <Button
                            variant="outlined"
                            startIcon={<OpenInNewIcon />}
                            size="small"
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            fullWidth
                          >
                            Перейти на сайт
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Container>
      </Box>

      <Footer />

      {/* Модальное окно с организациями по выбранной категории */}
      <Dialog open={Boolean(activeCategory)} onClose={handleCloseCategory} maxWidth="sm" fullWidth>
        {activeCategory && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: `${activeCategory.color}1A`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <activeCategory.icon sx={{ color: activeCategory.color, fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {activeCategory.name}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseCategory} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {itemsForActiveCategory.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  Информация по этому направлению скоро появится.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {itemsForActiveCategory.map((item) => (
                    <Box key={item._id}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {item.title}
                      </Typography>
                      {item.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: item.url ? 1.5 : 0, whiteSpace: 'pre-line' }}
                        >
                          {item.description}
                        </Typography>
                      )}
                      {item.url && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<OpenInNewIcon />}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Перейти на сайт
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default UsefulResourcesPage;
